import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useFacebookMedia } from '@/hooks/useFacebookMedia';
import { motion } from 'framer-motion';
import { ChevronRight } from 'lucide-react';
import { memo, useState } from 'react';

export const PostPerformanceCard = memo(() => {
  const { data: media, isLoading } = useFacebookMedia();
  const [isOpen, setIsOpen] = useState(false);

  if (isLoading) {
    return <SkeletonLoader />;
  }

  // First get the 10 most recent posts
  const recentPosts = (media || [])
    ?.sort((a, b) => {
      const aTime = new Date(a.timestamp).getTime();
      const bTime = new Date(b.timestamp).getTime();

      return bTime - aTime; // Most recent first
    })
    .slice(0, 10);

  const latestPost = recentPosts?.[0]; // Get the most recent post

  // Sort the 10 recent posts by reach for ranking
  const sortedByReach = [...(recentPosts || [])].sort((a, b) => {
    const aReach =
      a.media_insights.find((i) => i.name === 'reach')?.value?.value || 0;
    const bReach =
      b.media_insights.find((i) => i.name === 'reach')?.value?.value || 0;

    return bReach - aReach;
  });

  if (!latestPost || !sortedByReach) {
    return null;
  }

  // Find rank of latest post among the 10 most recent posts
  const latestPostRank =
    sortedByReach.findIndex((post) => post.id === latestPost.id) + 1;
  const totalPosts = sortedByReach.length;

  // Use the sorted recent posts for the tooltip
  const topPosts = sortedByReach.map((post) => ({
    id: post.id,
    title: post.caption || post.media_description || 'Untitled Post',
    views:
      post.media_insights.find((i) => i.name === 'reach')?.value?.value || '0',
    thumbnail:
      post.thumbnail_url || post.media_url || 'https://placehold.co/400',
    timestamp: post.timestamp,
  }));

  const reach =
    latestPost.media_insights.find((i) => i.name === 'reach')?.value?.value ||
    '0';

  // Get likes and comments directly from media_metrics object
  const likes = latestPost.media_metrics?.like_count || '0';
  const comments = latestPost.media_metrics?.comments_count || '0';

  return (
    <Card className="p-5 h-max w-full">
      <div className="flex flex-col gap-4">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <h2 className="text-lg font-semibold">Latest Instagram Post Performance</h2>
        </motion.div>
        <div className="relative w-full pb-[100%] flex flex-col">
          <div className="absolute inset-0">
            <img
              src={
                latestPost.thumbnail_url ||
                latestPost.media_url ||
                'https://placehold.co/400'
              }
              alt="Latest post thumbnail"
              className="w-full h-full object-cover rounded-lg"
            />
            <div className="absolute bottom-0 left-0 p-4 w-full bg-gradient-to-t from-black/60 to-transparent rounded-b-lg">
              <h3 className="text-white text-sm font-medium line-clamp-2">
                {latestPost.caption ||
                  latestPost.media_description ||
                  'Untitled Post'}
              </h3>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">Ranking by reach</span>
          <TooltipProvider>
            <Tooltip
              open={isOpen}
              onOpenChange={setIsOpen}
              disableHoverableContent
              delayDuration={99999999}>
              <TooltipTrigger
                className="flex items-center gap-1"
                onClick={() => setIsOpen(!isOpen)}>
                <span className="text-sm font-bold">
                  {latestPostRank > totalPosts ? '10+' : latestPostRank} of{' '}
                  {totalPosts}
                </span>
                <ChevronRight className="w-4 h-4" />
              </TooltipTrigger>
              <TooltipContent className="w-80 py-4">
                <div className="space-y-2">
                  <h3 className="font-bold">Top recent posts</h3>
                  <div className="space-y-2">
                    {topPosts.map((post, index) => (
                      <div
                        key={post.id}
                        className={`flex items-center px-4 gap-2 ${
                          post.id === latestPost.id
                            ? 'bg-gray-100 rounded p-1'
                            : ''
                        }`}>
                        <span className="text-sm min-w-[20px]">
                          {index + 1}
                        </span>
                        <div className="w-8 h-8 bg-gray-200 rounded overflow-hidden">
                          <img
                            src={post.thumbnail}
                            alt=""
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <span className="text-sm flex-1 truncate">
                          {post.title}
                        </span>
                        <span className="text-sm text-gray-600">
                          {Number(post.views).toLocaleString()}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        <div className="flex flex-col space-y-3">
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-600">Reach</p>
            <p className="text-sm">{reach}</p>
          </div>
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-600">Likes</p>
            <p className="text-sm">{likes}</p>
          </div>
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-600">Comments</p>
            <p className="text-sm">{comments}</p>
          </div>
        </div>

        {latestPost.media_url && (
          <Button
            variant="ghost"
            className="w-full"
            onClick={() => window.open(latestPost.media_url)}>
            Go to Post
          </Button>
        )}
      </div>
    </Card>
  );
});

const SkeletonLoader = () => {
  return (
    <Card className="p-5 h-max w-full">
      <div className="flex flex-col gap-4">
        <Skeleton className="h-7 w-48" />
        <Skeleton className="aspect-square w-full rounded-lg" />

        <div className="flex items-center justify-between">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-16" />
        </div>

        <div className="flex flex-col space-y-3">
          <div className="flex justify-between items-center">
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-4 w-12" />
          </div>
          <div className="flex justify-between items-center">
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-4 w-12" />
          </div>
          <div className="flex justify-between items-center">
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-4 w-12" />
          </div>
        </div>

        <Skeleton className="h-10 w-full" />
      </div>
    </Card>
  );
};
